import React, { useEffect, useState } from 'react';
import { Play, Clock3 } from 'lucide-react';
import type { SupportVideo } from './support.data';
import { useSupportAssetUrl } from './support.urls';

interface SupportVideoCardProps {
  video: SupportVideo;
  onSelect: (video: SupportVideo) => void;
}

const SupportVideoCard: React.FC<SupportVideoCardProps> = ({ video, onSelect }) => {
  // Blob-relative thumbnails resolve to a user-SAS-signed URL; direct
  // references render synchronously. `undefined` omits src until resolved.
  const thumbnailSrc = useSupportAssetUrl(video.thumbnailUrl);
  const [imgLoaded, setImgLoaded] = useState(false);

  // A SAS refresh yields a new signed URL; show the placeholder again while
  // the re-resolved image loads.
  useEffect(() => {
    setImgLoaded(false);
  }, [thumbnailSrc]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(video);
    }
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
  };

  return (
    <article className="support-video-card">
      <button
        type="button"
        className="support-video-thumb-button"
        onClick={() => onSelect(video)}
        onKeyDown={handleKeyDown}
        aria-label={`Play tutorial: ${video.title}`}
      >
        <span className="support-video-thumb">
          {/* Blurred cover-fit backdrop fills the 16:9 frame behind the main
              image; for full-bleed 16:9 art it stays invisible. Empty canvas
              areas in the source file itself cannot be filled by CSS — see
              note below. Thumbnails must be exported full-bleed 16:9. */}
          <img
            className="support-video-thumb-bg"
            src={thumbnailSrc || undefined}
            alt=""
            aria-hidden="true"
            onError={handleImageError}
          />
          <img
            className="support-video-thumb-img"
            src={thumbnailSrc || undefined}
            alt=""
            aria-hidden="true"
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            onError={handleImageError}
          />
          {!imgLoaded && (
            <span className="support-video-thumb-fallback" aria-hidden="true">
              <Play size={20} strokeWidth={2} />
            </span>
          )}
          <span className="support-video-overlay" aria-hidden="true" />
          <span className="support-play-button" aria-hidden="true">
            <Play size={20} strokeWidth={2.2} fill="currentColor" />
          </span>
          {video.duration && (
            <span className="support-video-duration">
              <Clock3 size={12} strokeWidth={2.2} aria-hidden="true" />
              {video.duration}
            </span>
          )}
        </span>
      </button>
      <h3 className="support-video-title">{video.title}</h3>
      <p className="support-video-description">{video.description}</p>
    </article>
  );
};

export default SupportVideoCard;
